import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { noop } from 'lodash-es';
import { useStateInRef, useTransaction } from 'seagull';
import { useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { FeatureFlag } from '~/featureFlags';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useRootDevice } from '~/providers/DeviceProvider';
import { selectors } from '~/store/state-selectors';
import { secondsUntilJwtStale, STALE_JWT_THRESHOLD_SECONDS } from './intercom-jwt-staleness';
const IntercomStarterContext = /*#__PURE__*/ createContext({
    setEnableIntercom: noop
});
export const useIntercomStarter = ()=>useContext(IntercomStarterContext);
export function IntercomStarter(param) {
    let { children } = param;
    const userInfo = useStore((state)=>state.userInfo);
    const { getState } = useStoreStatic();
    const isRealUser = useStore(selectors.isRealUser);
    const { intercomClient, getNow } = useIoContext();
    const { refreshIntercomJwt } = useStoreActions();
    const rootDevice = useRootDevice();
    const getRootDevice = useStateInRef(rootDevice);
    const [enableIntercom, setEnableIntercom] = useState(false);
    const value = useMemo(()=>({
            setEnableIntercom
        }), []);
    const userEmail = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.email()) || '';
    const userId = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()) || '';
    const startTransaction = useTransaction();
    const featureFlags = useFeatureFlags();
    const featureFlagsLoaded = useStore((s)=>selectors.ff.featureFlags(s).data != null);
    const useJwtIdentity = featureFlags[FeatureFlag.INTERCOM_JWT_IDENTITY] === true;
    const businessId = useStore(selectors.user.businessId) || '';
    const lastModeRef = useRef('none');
    useEffect(()=>{
        let cancelled = false;
        if (!isRealUser || !enableIntercom) {
            if (lastModeRef.current !== 'shutdown') {
                intercomClient.shutdown();
                lastModeRef.current = 'shutdown';
            }
            return;
        }
        if (!featureFlagsLoaded) return;
        const hideDefaultLauncher = getRootDevice().isMobileDevice;
        const mode = useJwtIdentity ? 'jwt' : 'legacy';
        if (mode === 'legacy' && lastModeRef.current === 'legacy') return;
        if (lastModeRef.current === 'jwt' || lastModeRef.current === 'legacy') {
            intercomClient.shutdown();
        }
        const bootWithJwt = (jwt)=>intercomClient.boot({
                userId,
                email: userEmail,
                jwt,
                hideDefaultLauncher
            });
        if (mode === 'jwt') {
            const intercomUserJwt = selectors.intercomUserJwt(getState());
            if (intercomUserJwt == null) {
                lastModeRef.current = 'shutdown';
                return;
            }
            const receivedAt = selectors.intercomUserJwtReceivedAt(getState());
            const stalenessSeconds = secondsUntilJwtStale(intercomUserJwt, receivedAt, getNow().getTime());
            const isStale = stalenessSeconds != null && stalenessSeconds < STALE_JWT_THRESHOLD_SECONDS;
            if (isStale) {
                refreshIntercomJwt().then((refreshResult)=>{
                    if (cancelled) return;
                    const refreshedJwt = selectors.intercomUserJwt(getState());
                    const refreshedReceivedAt = selectors.intercomUserJwtReceivedAt(getState());
                    const refreshedStaleness = secondsUntilJwtStale(refreshedJwt, refreshedReceivedAt, getNow().getTime());
                    const stillStale = refreshedStaleness == null || refreshedStaleness < STALE_JWT_THRESHOLD_SECONDS;
                    if (stillStale) {
                        if (refreshResult.receivedUserInfo) {
                            Sentry.captureMessage('Intercom JWT near or past expiry at boot', {
                                level: 'warning',
                                tags: {
                                    context: 'intercom_jwt_stale'
                                },
                                extra: {
                                    userId,
                                    userEmail,
                                    secondsUntilExpiry: refreshedStaleness,
                                    jwtPresentInResponse: refreshResult.jwtPresentInResponse
                                }
                            });
                        } else {
                            var _refreshResult_httpStatus;
                            Sentry.captureMessage('Intercom JWT refresh failed at boot', {
                                level: 'warning',
                                tags: {
                                    context: 'intercom_jwt_refresh_failed'
                                },
                                extra: {
                                    userId,
                                    userEmail,
                                    refetchStatus: (_refreshResult_httpStatus = refreshResult.httpStatus) !== null && _refreshResult_httpStatus !== void 0 ? _refreshResult_httpStatus : 'no-response',
                                    errorMessage: refreshResult.errorMessage
                                }
                            });
                        }
                    }
                    bootWithJwt(refreshedJwt !== null && refreshedJwt !== void 0 ? refreshedJwt : undefined);
                });
            } else {
                bootWithJwt(intercomUserJwt !== null && intercomUserJwt !== void 0 ? intercomUserJwt : undefined);
            }
        } else {
            const userHash = selectors.intercomUserHash(getState());
            if (!userHash) {
                Sentry.captureMessage('Intercom boot without user_hash', {
                    level: 'warning',
                    tags: {
                        context: 'intercom_missing_user_hash'
                    },
                    extra: {
                        userId,
                        userEmail
                    }
                });
            }
            intercomClient.boot({
                userId,
                email: userEmail,
                userHash: userHash || undefined,
                hideDefaultLauncher
            });
        }
        lastModeRef.current = mode;
        return ()=>{
            cancelled = true;
        };
    }, [
        userEmail,
        userId,
        businessId,
        isRealUser,
        enableIntercom,
        useJwtIdentity,
        featureFlagsLoaded,
        startTransaction
    ]);
    return /*#__PURE__*/ _jsx(IntercomStarterContext.Provider, {
        value: value,
        children: children
    });
}
